import {
  catchError,
  concatMap,
  defer,
  from,
  map,
  Observable,
  of,
  reduce,
  tap,
  throwError,
} from "rxjs";
import type {
  ReceiverRecommendationResult,
  SendRecommendationResponse,
  WorkRecommendationDto,
} from "src/app/models/WorksModel";
import {WORK_BULK_BATCH_SIZE} from "../constants/work-bulk-actions.constants";

/** Stop new requests after a failure, but return already committed successes. */
export function runWorkBulkBatches<T, R>(
  items: T[],
  request: (batch: T[]) => Observable<R[]>,
  failed: (batch: T[]) => R[]
): Observable<R[]> {
  return defer(() => {
    const batches: T[][] = [];
    for (let offset = 0; offset < items.length; offset += WORK_BULK_BATCH_SIZE)
      batches.push(items.slice(offset, offset + WORK_BULK_BATCH_SIZE));
    let stopped = false;
    let completedBatch = false;
    return from(batches).pipe(
      concatMap((batch) =>
        stopped
          ? of(failed(batch))
          : request(batch).pipe(
              tap(() => {
                completedBatch = true;
              }),
              catchError((error: unknown) => {
                if (!completedBatch) return throwError(() => error);
                stopped = true;
                return of(failed(batch));
              })
            )
      ),
      reduce((results, batch) => {
        results.push(...batch);
        return results;
      }, [] as R[])
    );
  });
}

export function sendWorkRecommendationBatches(
  dto: WorkRecommendationDto,
  request: (
    batch: WorkRecommendationDto
  ) => Observable<SendRecommendationResponse>
): Observable<SendRecommendationResponse> {
  return runWorkBulkBatches(
    dto.works,
    (works) =>
      request({...dto, works}).pipe(map((response) => response.results)),
    (works): ReceiverRecommendationResult[] =>
      dto.receivers.map((receiverId) => ({
        receiverId,
        successCount: 0,
        works: works.map((work) => ({
          work,
          status: "error",
          error: "UNKNOWN_ERROR",
        })),
      }))
  ).pipe(
    map((batches) => {
      const receivers = new Map<string, ReceiverRecommendationResult>();
      for (const result of batches) {
        const previous = receivers.get(result.receiverId);
        if (!previous)
          receivers.set(result.receiverId, {
            ...result,
            works: [...result.works],
          });
        else {
          previous.successCount += result.successCount;
          previous.works.push(...result.works);
        }
      }
      return {results: [...receivers.values()]};
    })
  );
}
